import React, { useContext, useEffect, useRef, useState } from 'react';
import { Place } from '../../../app/models/Place';
import { useHistory } from 'react-router-dom';
import { Unit } from '../../../app/models/Unit';
import { Currency } from '../../../app/models/Currency';
import useRouteSegmentService from '../../../app/services/hooks/useRouteSegmentService';
import {
    RouteSegment,
    RouteSegmentDataForm,
    defaultRouteSegmentDataForm
} from '../../../app/models/RouteSegment';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { toast } from 'react-toastify';
import LazyLoading from '../../../components/LazyLoading';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';
import RouteSegmentForm from './forms/RouteSegmentForm';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import useQuoteRouteSegmentService from '../../../app/services/hooks/useQuoteRouteSegmentService';
import {
    QuoteRouteSegmentDataForm,
    defaultQuoteRouteSegmentDataForm
} from '../../../app/models/QuoteRouteSegment';
import QuoteRouteSegmentForm from './forms/QuoteRouteSegmentForm';
import { AppContext } from '../../../contexts/AppContext';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';

interface Props {
    quoteId?: number;
    // currencyId?: number;
    onSaved?: () => void;
    onCancel?: () => void;
    onError?: () => void;
}

const QuoteRouteSegmentCreate = ({
    quoteId,
    //   currencyId,
    onSaved,
    onCancel,
    onError
}: Props) => {
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const [units, setUnits] = useState<Unit[]>([]);
    const [currencies, setCurrencies] = useState<Currency[]>([]);
    const [errorGetData, setErrorGetData] = useState('');
    const [routeSegments, setRouteSegments] = useState<RouteSegment[]>([]);
    const [quoteRouteSegment, setQuoteRouteSegment] = useState<QuoteRouteSegmentDataForm>({
        ...defaultQuoteRouteSegmentDataForm,
        ['quote_id']: quoteId,
        ['route_segment_id']: undefined,
        ['agreed_distance']: 0
    });

    const [errorFields, setErrorFields] = useState<any>();

    const {
        fetchingStoreQuoteRouteSegment,
        storeQuoteRouteSegment,
        fetchingCreateQuoteRouteSegment,
        createQuoteRouteSegment,
        fetchingShowRsToQuoteRouteSegment,
        showRsToQuoteRouteSegment
    } = useQuoteRouteSegmentService();

    const routeSegmentIdSelected = useRef<number | undefined>(0);

    useEffect(() => {
        if (showLoading) showLoading('loading', 'Cargando datos...');
        createQuoteRouteSegment({
            onSuccess: (response: ServiceResponse) => {
                setUnits(response.data.units);
                setCurrencies(response.data.currencies);
                setRouteSegments(response.data.route_segments);
                if (hideLoading) hideLoading();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                toast.error(response.message);
                setErrorGetData(response.message);
                if (onError) onError();
            }
        });
    }, []);

    useEffect(() => {
        if (quoteRouteSegment.route_segment_id == undefined) {
            routeSegmentIdSelected.current = undefined;
        }
        if (
            quoteRouteSegment.route_segment_id &&
            quoteRouteSegment.route_segment_id != routeSegmentIdSelected.current
        ) {
            showRsToQuoteRouteSegment(0, quoteRouteSegment.route_segment_id, {
                onSuccess: (response: ServiceResponse) => {
                    setErrorFields(undefined);
                    routeSegmentIdSelected.current = quoteRouteSegment.route_segment_id ?? 0;
                    setQuoteRouteSegment({
                        ...quoteRouteSegment,
                        ...defaultQuoteRouteSegmentDataForm,
                        ...response.data.route_segment
                    });
                },
                onError: () => {
                    setQuoteRouteSegment({
                        ...quoteRouteSegment,
                        route_segment_id: routeSegmentIdSelected.current
                    });
                }
            });
        }
    }, [quoteRouteSegment.route_segment_id]);

    const store = () => {
        if (showLoading) showLoading('loading', 'Guardando opción de tramo...');

        storeQuoteRouteSegment(quoteRouteSegment, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });
                toast.success(response.message, {
                    autoClose: 2500
                });
                if (onSaved) onSaved();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            },
            onFieldError: (errorFields: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setErrorFields(errorFields.data);
            }
        });
    };

    const cancel = () => {
        if (onCancel) onCancel();
    };

    if (errorGetData !== '') {
        return <div>{errorGetData}</div>;
    }

    return fetchingCreateQuoteRouteSegment || fetchingShowRsToQuoteRouteSegment ? (
        <LazyLoading height={300} />
    ) : (
        <>
            <div className="row">
                <div className="col-12">
                    <QuoteRouteSegmentForm
                        quoteRouteSegmentForm={quoteRouteSegment}
                        setQuoteRouteSegmentForm={setQuoteRouteSegment}
                        routeSegments={routeSegments}
                        currencies={currencies}
                        units={units}
                        errorFields={errorFields}
                    />
                </div>
            </div>
            <hr />
            <div className="row justify-content-end">
                <div className="col-auto">
                    <ButtonCancelForm
                        callbackCancel={cancel}
                        locked={fetchingStoreQuoteRouteSegment}
                    />
                </div>
                <div className="col-auto">
                    <ButtonSaveForm callbackSave={store} locked={fetchingStoreQuoteRouteSegment} />
                </div>
            </div>
        </>
    );
};

export default QuoteRouteSegmentCreate;
